import { onMounted, reactive, ref, watch } from 'vue';
import { watchDebounced } from '@vueuse/core';
import { toast } from 'vue-sonner';
import useApi from '@/composables/useApi.ts';
import useApp from '@/composables/useApp.ts';
import { uuid } from '@/lib/uuid.ts';
import type {
    EntrySearchResult,
    FileSearchResult,
    SearchRecommendations,
} from '@/types';

interface SearchResponse {
    results: {
        entries: EntrySearchResult[];
        files: FileSearchResult[];
    };
    count: number;
}

const recommendations = reactive<SearchRecommendations>({
    recentSearches: [],
    presets: [],
});

export default function useSearch() {
    const query = ref('');

    /**
     * Identifies one search, from the first keystroke to the click that ends it.
     *
     * The search fires on every keystroke behind a debounce, so "Arbeitgeber" produces requests
     * for "Arbeit", "Arbeitge", "Arbeitgeber". The sequence id lets the server keep those as one
     * record instead of three fragments, and lets the click be attributed to the search it came
     * from. A new one begins whenever a search ends - by a click or by clearing the field.
     */
    const sequenceId = ref(uuid());
    const isPending = ref(false);
    const count = ref(0);
    const results = ref<SearchResponse['results']>({
        entries: [],
        files: [],
    });

    const useFetch = useApi();
    const app = useApp();

    async function performSearch(query: string) {
        const params = new URLSearchParams({
            q: query,
            sequence: sequenceId.value,
        });

        const { data, error } = await useFetch<SearchResponse>(
            'search?' + params.toString()
        );

        if (error.value) {
            toast.error('Suchergebnisse konnten nicht geladen werden.');
            console.error(error.value);
            return;
        }

        results.value = data.value?.results as SearchResponse['results'];
        count.value = data.value?.count as SearchResponse['count'];
    }

    /**
     * Report that the user opened one of the results.
     *
     * Deliberately not skipped for a repeated term. The list of recent searches must not grow a
     * duplicate, but the report has to go out every time: leaving it out would make a second
     * search for the same word look like nobody clicked, and that search would be counted as
     * abandoned.
     *
     * Sent as a beacon because a result that is a page navigates away, and the browser is free to
     * cancel a request in flight when it does. A beacon survives the navigation.
     */
    function track(uid?: string) {
        const term = query.value;

        if (term === '') {
            return;
        }

        const body = new FormData();
        body.append('searchTerm', term);
        body.append('sequence', sequenceId.value);
        body.append(app.csrf.name, app.csrf.token);

        if (uid) {
            body.append('uid', uid);
        }

        const url = app.site.url + 'api/track-search';

        // Falls back to fetch where sendBeacon is missing or refuses the payload. The click may
        // then be lost to a navigation, which costs one data point - never the navigation itself.
        if (!navigator.sendBeacon?.(url, body)) {
            fetch(url, { method: 'POST', body, keepalive: true }).catch(() => {});
        }

        if (!recommendations.recentSearches.includes(term)) {
            recommendations.recentSearches = [
                term,
                ...recommendations.recentSearches.slice(0, 2),
            ];
        }

        // The search is over; whatever the user types next is a new one.
        sequenceId.value = uuid();
    }

    function reset() {
        isPending.value = false;
        sequenceId.value = uuid();
        query.value = '';
        results.value = {
            entries: [],
            files: [],
        };
        count.value = 0;
    }

    watch(query, (newValue, oldValue) => {
        if (newValue !== oldValue) {
            isPending.value = true;
        }
    });

    watchDebounced(
        query,
        async (value) => {
            await performSearch(value);
            isPending.value = false;
        },
        { debounce: 250 }
    );

    onMounted(async () => {
        // fetch search recommendations after page has rendered to not block requests
        const { data } = await useFetch<SearchRecommendations>(
            'search/recommendations'
        );

        recommendations.recentSearches = data.value
            ?.recentSearches as SearchRecommendations['recentSearches'];
        recommendations.presets = data.value
            ?.presets as SearchRecommendations['presets'];
    });

    return {
        query,
        results,
        count,
        recommendations,
        isPending,
        reset,
        track,
    };
}
